import { getTenantSlug } from './tenant.js';

/**
 * Invitation à envoyer un retour (FeedbackNudge) : quand la proposer.
 *
 * - jamais le jour de la première venue (on découvre le campus) ;
 * - après 10 minutes passées dans le campus pendant la visite en cours, onglet visible ;
 * - puis toutes les 2 semaines au plus. Chaque « Plus tard » d'affilée double
 *   l'écart (2, 4, 8 semaines, au plus 60 jours), remis à zéro par un retour envoyé ;
 * - un mois de pause après un retour envoyé, quel que soit le chemin (menu ou invitation) ;
 * - « Ne plus me le proposer » l'arrête pour ce compte (le menu de l'avatar reste).
 *
 * Retenu dans le localStorage, par compte et par campus : une préférence, pas
 * une donnée à garantir (un autre appareil repart de zéro).
 */

const DAY = 24 * 60 * 60 * 1000;
export const NUDGE_SESSION_MIN_MS = 10 * 60 * 1000;
const NUDGE_INTERVAL_MS = 14 * DAY;
const NUDGE_INTERVAL_MAX_MS = 60 * DAY;
const NUDGE_AFTER_SENT_MS = 30 * DAY;

interface NudgeState {
  /** Jour de la première venue vue par ce navigateur (AAAA-MM-JJ, heure locale). */
  firstDay?: string;
  lastShown?: number;
  lastSent?: number;
  /** « Plus tard » d'affilée depuis le dernier retour envoyé. */
  dismissals?: number;
  optOut?: boolean;
}

const key = (userId: string) => `placio:feedback-nudge:${getTenantSlug()}:${userId}`;

function read(userId: string): NudgeState {
  try { return JSON.parse(localStorage.getItem(key(userId)) ?? '{}') as NudgeState; } catch { return {}; }
}
function write(userId: string, patch: Partial<NudgeState>): void {
  try { localStorage.setItem(key(userId), JSON.stringify({ ...read(userId), ...patch })); } catch { /* navigation privée */ }
}

function localDay(t: number): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** À appeler à l'entrée dans le campus : retient le jour de la première venue. */
export function noteVisit(userId: string, now = Date.now()): void {
  if (!read(userId).firstDay) write(userId, { firstDay: localDay(now) });
}

/** L'invitation est-elle due (hors durée de la visite, vérifiée par l'appelant) ? */
export function isNudgeDue(userId: string, now = Date.now()): boolean {
  const s = read(userId);
  if (s.optOut || !s.firstDay || localDay(now) <= s.firstDay) return false;
  if (s.lastSent && now - s.lastSent < NUDGE_AFTER_SENT_MS) return false;
  const interval = Math.min(NUDGE_INTERVAL_MS * 2 ** (s.dismissals ?? 0), NUDGE_INTERVAL_MAX_MS);
  return !s.lastShown || now - s.lastShown >= interval;
}

export function markNudgeShown(userId: string, now = Date.now()): void { write(userId, { lastShown: now }); }
export function markNudgeDismissed(userId: string): void { write(userId, { dismissals: (read(userId).dismissals ?? 0) + 1 }); }
export function markNudgeOptOut(userId: string): void { write(userId, { optOut: true }); }
/** Un retour envoyé (menu ou invitation) : pause d'un mois, écart remis à 2 semaines. */
export function markFeedbackSent(userId: string, now = Date.now()): void { write(userId, { lastSent: now, dismissals: 0 }); }
